import { z } from 'zod';

/**
 * #130: Notizen der Verlaufsereignisse aus dem Salesforce-Sync (`project_history.note`, JSON).
 * Schreiber: `apps/api/src/sync/zuordnungs-historie.ts`; Leser: Verlauf-Tab im Projektdetail.
 * Personennamen stehen bewusst im Klartext (Entscheidung 13.10.): der Verlauf soll ohne
 * Nachschlagen lesbar sein, auch wenn eine Person später inaktiv wird.
 */

/** Felder, deren Änderung in SF einen Eintrag `sf_zuordnung_geaendert` erzeugt. */
export const ZUORDNUNGS_FELDER = [
  'segment',
  'bereich',
  'dienstleistungsart',
  'projektleitung',
  'manager',
] as const;
export const ZuordnungsFeldSchema = z.enum(ZUORDNUNGS_FELDER);
export type ZuordnungsFeld = z.infer<typeof ZuordnungsFeldSchema>;

/** `sf_importiert`: Stand beim Anlegen des Projekts aus Salesforce. */
export const SfImportiertNotizSchema = z.object({
  project_key: z.string().min(1),
  segment: z.string().nullable(),
  bereich: z.string().nullable(),
  dienstleistungsart: z.string().nullable(),
  projektleitung: z.string().nullable(),
  manager: z.string().nullable(),
  sync_run_id: z.string().nullable(),
});
export type SfImportiertNotiz = z.infer<typeof SfImportiertNotizSchema>;

/** `sf_zuordnung_geaendert`: ein Feld, alt → neu, am Auftrag `project_key`. */
export const SfZuordnungGeaendertNotizSchema = z.object({
  project_key: z.string().min(1),
  feld: ZuordnungsFeldSchema,
  alt: z.string().nullable(),
  neu: z.string().nullable(),
});
export type SfZuordnungGeaendertNotiz = z.infer<typeof SfZuordnungGeaendertNotizSchema>;

/**
 * #158: Notiz der Besetzungs-Ereignisse (`besetzung_angelegt`, `besetzung_status_geaendert`,
 * `besetzung_zugeordnet`, `besetzung_verknuepft`, `besetzung_geloescht`), JSON in `project_history.note`.
 * `person` im Klartext (wie bei der Sync-Historie). `automatisch` kennzeichnet Übernahmen ohne Handlung.
 */
export const BesetzungNotizSchema = z.object({
  besetzung_id: z.string().uuid(),
  person: z.string(),
  feld: z.enum(['status', 'position', 'bedarf', 'sf']).optional(),
  alt: z.string().nullable(),
  neu: z.string().nullable(),
  automatisch: z.literal(true).optional(),
});
export type BesetzungNotiz = z.infer<typeof BesetzungNotizSchema>;

/**
 * #158: Notiz des Ereignisses `bedarf_zugeordnet` (Position am Bedarf geändert, auch beim Löschen
 * der Position). `alt`/`neu` sind die Stufen der Positionen (Klartext), `null` = nicht zugeordnet.
 */
export const BedarfZugeordnetNotizSchema = z.object({
  staffing_request_id: z.string().uuid(),
  rolle: z.string(),
  alt: z.string().nullable(),
  neu: z.string().nullable(),
});
export type BedarfZugeordnetNotiz = z.infer<typeof BedarfZugeordnetNotizSchema>;
